/**
 * Selection Sticky Header Component
 *
 * Shows the currently selected creature in a compact header when the
 * bottom sheet is collapsed. Provides visual feedback for the tap-to-select flow.
 *
 * Features:
 * - Shows creature sprite and name
 * - Clear selection button (X)
 * - Tap anywhere to keep and continue placing
 */

import React from 'react';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import './SelectionStickyHeader.css';

export interface SelectionStickyHeaderProps {
  /** The selected creature */
  creature: SerializedCreatureDocument;
  /** Called to clear the selection */
  onClear?: () => void;
}

export const SelectionStickyHeader: React.FC<SelectionStickyHeaderProps> = ({
  creature,
  onClear,
}) => {
  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  return (
    <div className="selection-header">
      <div className="selection-content">
        {/* Creature sprite */}
        <div className="selection-sprite">
          <img
            src={creature.sprites.menuSprite}
            alt={creature.name}
            draggable={false}
          />
        </div>

        {/* Creature info */}
        <div className="selection-info">
          <span className="selection-label">Selected:</span>
          <span className="selection-name">{creature.name}</span>
        </div>

        {/* Placement hint */}
        <div className="selection-hint">
          Tap a hex to place
        </div>
      </div>

      {/* Clear button */}
      <button
        className="selection-clear"
        onClick={handleClearClick}
        aria-label="Clear selection"
        title="Clear selection"
      >
        <span className="clear-icon">&times;</span>
      </button>
    </div>
  );
};

export default SelectionStickyHeader;
